import React, { useState, useEffect } from 'react';
import { ApiClient } from '../../api/client';
import { DEPLOYMENT, isOnHost, pointsTo } from '../../config/deployment';
import type { Tenant } from '../../types';
import {
  GitCompare,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Copy,
  Check,
  Building2,
  Trash2,
  Layers,
  UploadCloud,
  ClipboardPaste,
  X
} from 'lucide-react';

interface DiffField {
  field: string;
  label: string;
  local_val: any;
  remote_val: any;
}

interface DifferenceItem {
  slug: string;
  name: string;
  local: Tenant;
  remote: Tenant;
  diff_fields: DiffField[];
}

interface DiffResult {
  ok: boolean;
  stats: {
    local_count: number;
    remote_count: number;
    local_only_count: number;
    remote_only_count: number;
    difference_count: number;
    identical_count: number;
    remote_url: string;
    checked_at: string;
  };
  local_only: Tenant[];
  remote_only: Tenant[];
  differences: DifferenceItem[];
  identical: Array<{
    slug: string;
    name: string;
    tenant: Tenant;
  }>;
  all_local: Tenant[];
  all_remote: Tenant[];
}

interface TenantDiffModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTenantDeleted?: () => void;
}

export const TenantDiffModal: React.FC<TenantDiffModalProps> = ({
  isOpen,
  onClose,
  onTenantDeleted,
}) => {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const isLocalHost = host.includes('localhost') || host.includes('127.0.0.1');
  // S14: a címek a build-konfigurációból jönnek (config/deployment.ts, VITE_* változók),
  // nem a forráskódba írva.
  const isVps = isOnHost(DEPLOYMENT.vpsUrl) || host.includes('sslip.io');

  // Intelligens alapértelmezett Cél URL:
  // - Ha VPS-en vagy helyi gépen vagyunk -> Éles felhő (VITE_PRODUCTION_URL)
  // - Ha az éles felhőn vagyunk -> saját VPS (VITE_VPS_URL)
  const defaultTarget = (isLocalHost || isVps) ? DEPLOYMENT.productionUrl : DEPLOYMENT.vpsUrl;

  // S1: a szinkronizációs kulcs NEM lehet a forráskódban (a publikus bundle-ből kiolvasható
  // lenne) – a superadmin adja meg kézzel; a mező a munkamenet idejére megmarad.
  const defaultSecret = '';

  const [remoteUrl, setRemoteUrl] = useState(defaultTarget);
  const [secretKey, setSecretKey] = useState(defaultSecret);
  const [showConfig, setShowConfig] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [pushSuccess, setPushSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [diffData, setDiffData] = useState<DiffResult | null>(null);
  const [activeTab, setActiveTab] = useState<'local_only' | 'differences' | 'remote_only' | 'identical' | 'all'>('local_only');
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedSnapshot, setCopiedSnapshot] = useState(false);
  const [pasteModalOpen, setPasteModalOpen] = useState(false);
  const [pasteJsonText, setPasteJsonText] = useState('');

  const runDiff = async (customSnapshot?: Tenant[], overrideUrl?: string, overrideSecret?: string) => {
    setLoading(true);
    setErrorMsg(null);
    const target = overrideUrl || remoteUrl;
    const secret = overrideSecret || secretKey;
    try {
      const res: any = await ApiClient.compareTenantsWithRemote(target, secret, customSnapshot);
      const payload: DiffResult | null = res?.data || (res?.stats ? res : null);
      if (res?.ok && payload && payload.stats) {
        setDiffData(payload);
        
        // Ha helyi gépen futunk és az éles a cél, a lekérdezett helyi szervezeteket automatikusan beküldjük az élesnek a háttérben
        if (isLocalHost && pointsTo(target, DEPLOYMENT.productionUrl) && payload.all_local && payload.all_local.length > 0) {
          ApiClient.pushLocalSnapshotToRemote(target, secret, payload.all_local)
            .then(pushRes => {
              if (pushRes?.ok) {
                setPushSuccess(true);
                setTimeout(() => setPushSuccess(false), 4000);
              }
            })
            .catch(() => {});
        }

        // Automatikusan a legfontosabb fülre állunk
        if (payload.local_only && payload.local_only.length > 0) {
          setActiveTab('local_only');
        } else if (payload.differences && payload.differences.length > 0) {
          setActiveTab('differences');
        } else if (payload.remote_only && payload.remote_only.length > 0) {
          setActiveTab('remote_only');
        } else {
          setActiveTab('identical');
        }
      } else {
        setErrorMsg(res?.error || 'Nem sikerült az összehasonlítás a megadott távoli szerverrel.');
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Hálózati hiba történt a vizsgálat során.');
    } finally {
      setLoading(false);
    }
  };

  const handleManualPushToRemote = async () => {
    if (!diffData?.all_local || diffData.all_local.length === 0) return;
    setPushing(true);
    try {
      const res = await ApiClient.pushLocalSnapshotToRemote(remoteUrl, secretKey, diffData.all_local);
      if (res.ok) {
        setPushSuccess(true);
        alert(`Sikeresen beküldve az éles rendszerbe!\n\n${res.message || 'Az éles rendszer most már ismeri a helyi 16 szervezetet.'}`);
        setTimeout(() => setPushSuccess(false), 4000);
      } else {
        alert(res.error || 'Nem sikerült az állapot beküldése.');
      }
    } catch (e: any) {
      alert(`Hiba történt: ${e.message}`);
    } finally {
      setPushing(false);
    }
  };

  const handleCopySnapshotJson = () => {
    if (!diffData?.all_local) return;
    navigator.clipboard.writeText(JSON.stringify(diffData.all_local, null, 2));
    setCopiedSnapshot(true);
    setTimeout(() => setCopiedSnapshot(false), 2500);
  };

  const handleApplyPastedJson = () => {
    try {
      let parsed = JSON.parse(pasteJsonText);
      if (parsed && parsed.all_local) parsed = parsed.all_local;
      if (parsed && parsed.tenants) parsed = parsed.tenants;
      if (!Array.isArray(parsed) || parsed.length === 0) {
        alert('Érvénytelen szervezet JSON formátum!');
        return;
      }
      setPasteModalOpen(false);
      setPasteJsonText('');
      runDiff(parsed);
    } catch {
      alert('A beillesztett szöveg nem érvényes JSON.');
    }
  };

  useEffect(() => {
    if (isOpen) {
      runDiff();
    } else {
      setDiffData(null);
      setErrorMsg(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleDeleteLocalTenant = async (tenant: Tenant) => {
    if (!confirm(`Biztosan törölni szeretnéd a helyi adatbázisból a következő szervezetet?\n\n"${tenant.name}" (${tenant.slug}, ID: ${tenant.id})\n\nFigyelem: Ez véglegesen törli a szervezetet a helyi környezetből, így az egyezni fog az élessel!`)) {
      return;
    }

    setDeletingId(tenant.id);
    try {
      const res = await ApiClient.deleteTenant(tenant.id);
      if (res.ok) {
        alert(`A(z) "${tenant.name}" szervezet sikeresen törölve a helyi adatbázisból!`);
        if (onTenantDeleted) onTenantDeleted();
        runDiff();
      } else {
        alert(res.error || 'A törlés sikertelen.');
      }
    } catch (e: any) {
      alert(`Hiba történt: ${e.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  const handleCopyReport = () => {
    if (!diffData) return;
    const s = diffData.stats;
    let text = `====================================================\n`;
    text += `KÖZZÉTÉR – KÖRNYEZETI SZERVEZET-ÖSSZEHASONLÍTÁS (DIFF)\n`;
    text += `Vizsgálat ideje: ${new Date(s.checked_at).toLocaleString('hu-HU')}\n`;
    text += `Cél környezet: ${s.remote_url}\n`;
    text += `====================================================\n\n`;
    text += `STATISZTIKA:\n`;
    text += `- Helyi (Local) szervezetek: ${s.local_count} db\n`;
    text += `- Távoli (Éles) szervezetek: ${s.remote_count} db\n`;
    text += `- Csak Local-ban létezik: ${s.local_only_count} db\n`;
    text += `- Csak Élesben létezik: ${s.remote_only_count} db\n`;
    text += `- Mezőbeli eltéréssel: ${s.difference_count} db\n`;
    text += `- Teljesen megegyező: ${s.identical_count} db\n\n`;

    if (diffData.local_only.length > 0) {
      text += `--- CSAK LOCAL-BAN LÉTEZIK (${diffData.local_only.length} db) ---\n`;
      diffData.local_only.forEach(t => {
        text += `• ID: ${t.id} | Slug: ${t.slug} | Név: ${t.name} (DEMO: ${t.is_demo ? 'Igen' : 'Nem'})\n`;
      });
      text += `\n`;
    }

    if (diffData.remote_only.length > 0) {
      text += `--- CSAK ÉLESBEN LÉTEZIK (${diffData.remote_only.length} db) ---\n`;
      diffData.remote_only.forEach(t => {
        text += `• ID: ${t.id} | Slug: ${t.slug} | Név: ${t.name}\n`;
      });
      text += `\n`;
    }

    if (diffData.differences.length > 0) {
      text += `--- MEZŐKBEN ELTÉRŐ SZERVEZETEK (${diffData.differences.length} db) ---\n`;
      diffData.differences.forEach(d => {
        text += `• ${d.name} (${d.slug}):\n`;
        d.diff_fields.forEach(f => {
          text += `    - ${f.label} (${f.field}): Local [${f.local_val}] ≠ Éles [${f.remote_val}]\n`;
        });
      });
      text += `\n`;
    }

    text += `--- TELJESEN EGYEZŐ SZERVEZETEK (${diffData.identical.length} db) ---\n`;
    diffData.identical.forEach(i => {
      text += `✓ ${i.name} (${i.slug})\n`;
    });

    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-xs overflow-y-auto">
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[92vh] flex flex-col overflow-hidden animate-in fade-in duration-200">
        
        {/* Modal Fejléc */}
        <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex items-start justify-between gap-4 bg-gradient-to-r from-indigo-50/70 via-white to-purple-50/70 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
          <div className="flex items-start gap-3">
            <div className="p-2.5 bg-indigo-600 text-white rounded-xl shadow-md">
              <GitCompare className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                  Környezeti Szervezet-Összehasonlító (Local ↔ Éles)
                </h2>
                <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-indigo-100 text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-300">
                  SZUPERADMIN
                </span>
                <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                  {isLocalHost ? '🏠 Helyi (Local) környezet' : isVps ? '🖥️ Saját VPS Szerver' : '🌐 Éles Cloudflare Felhő'}
                </span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Kétirányú környezeti szervezet-összehasonlítás a helyi gép, a saját VPS és az éles felhő között.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg transition"
            title="Bezárás"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Környezeti konfigurációs sáv */}
        <div className="px-5 py-3 bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-800 space-y-2.5 text-xs">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-1 min-w-[280px]">
              <span className="font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap">
                Cél URL:
              </span>
              <input
                type="text"
                value={remoteUrl}
                onChange={(e) => setRemoteUrl(e.target.value)}
                className="flex-1 px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-800 dark:text-slate-200 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500"
                placeholder="https://portal.example.hu"
              />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Helyi felületről: Küldés az élesbe */}
              {isLocalHost && diffData && (
                <button
                  onClick={handleManualPushToRemote}
                  disabled={pushing}
                  className="flex items-center gap-1 px-2.5 py-1.5 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800 rounded-lg font-semibold transition cursor-pointer"
                  title="Helyi szervezetek állapotának beküldése az éles rendszerbe"
                >
                  {pushSuccess ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <UploadCloud className="w-3.5 h-3.5" />}
                  <span>{pushSuccess ? 'Élesbe beküldve!' : 'Állapot küldése az élesbe'}</span>
                </button>
              )}

              {/* Éles/VPS felületről: Beillesztés modal megnyitása */}
              <button
                onClick={() => setPasteModalOpen(true)}
                className="flex items-center gap-1 px-2.5 py-1.5 bg-sky-50 hover:bg-sky-100 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300 border border-sky-300 dark:border-sky-800 rounded-lg font-semibold transition cursor-pointer"
                title="Helyi JSON pillanatkép beillesztése"
              >
                <ClipboardPaste className="w-3.5 h-3.5" />
                <span>Pillanatkép beillesztése</span>
              </button>

              <button
                onClick={() => setShowConfig(!showConfig)}
                className="text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
              >
                {showConfig ? 'Kulcs elrejtése' : 'Secret kulcs...'}
              </button>

              <button
                onClick={() => runDiff()}
                disabled={loading}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg font-semibold transition cursor-pointer shadow-xs disabled:opacity-50"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                <span>Újravizsgálat</span>
              </button>
            </div>
          </div>

          {/* Gyorsválasztó gombok (Presets) */}
          <div className="flex items-center gap-2 pt-1 border-t border-slate-200/60 dark:border-slate-700/60 flex-wrap">
            <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">Gyors Célpont:</span>
            
            <button
              type="button"
              onClick={() => {
                const u = DEPLOYMENT.productionUrl;
                const s = secretKey;
                if (!u) { setShowConfig(true); setErrorMsg('Az éles cím nincs beállítva (VITE_PRODUCTION_URL) – add meg kézzel a Cél URL mezőben.'); return; }
                setRemoteUrl(u);
                setSecretKey(s);
                runDiff(undefined, u, s);
              }}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition cursor-pointer border flex items-center gap-1 ${
                pointsTo(remoteUrl, DEPLOYMENT.productionUrl)
                  ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs'
                  : 'bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              <span>🌐 Éles Cloudflare</span>
              <span className="text-[9px] opacity-75">(15 db)</span>
            </button>

            <button
              type="button"
              onClick={() => {
                const u = DEPLOYMENT.vpsUrl;
                const s = secretKey;
                if (!u) { setShowConfig(true); setErrorMsg('A VPS címe nincs beállítva (VITE_VPS_URL) – add meg kézzel a Cél URL mezőben.'); return; }
                setRemoteUrl(u);
                setSecretKey(s);
                runDiff(undefined, u, s);
              }}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition cursor-pointer border flex items-center gap-1 ${
                pointsTo(remoteUrl, DEPLOYMENT.vpsUrl)
                  ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs'
                  : 'bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              <span>🖥️ Saját VPS</span>
              <span className="text-[9px] opacity-75">(3 db)</span>
            </button>

            <button
              type="button"
              onClick={() => {
                const u = 'http://localhost:8788';
                const s = secretKey;
                setRemoteUrl(u);
                setSecretKey(s);
                runDiff(undefined, u, s);
              }}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition cursor-pointer border flex items-center gap-1 ${
                remoteUrl.includes('localhost')
                  ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs'
                  : 'bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              <span>🏠 Helyi gép</span>
              <span className="text-[9px] opacity-75">(localhost)</span>
            </button>
          </div>

          {showConfig && (
            <div className="w-full flex items-center gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
              <span className="font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap">
                Sync Key / Secret:
              </span>
              <input
                type="password"
                value={secretKey}
                onChange={(e) => setSecretKey(e.target.value)}
                className="flex-1 px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-800 dark:text-slate-200 text-xs font-mono"
              />
            </div>
          )}
        </div>

        {/* Modal Tartalom */}
        <div className="p-5 overflow-y-auto flex-1 space-y-5">
          {errorMsg && (
            <div className="p-4 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 rounded-xl flex items-start gap-3 text-rose-800 dark:text-rose-300 text-sm">
              <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="font-semibold">Hiba történt a vizsgálat során:</p>
                <p className="text-xs">{errorMsg}</p>
                {!isLocalHost && (
                  <p className="text-[11px] text-rose-700 dark:text-rose-400 pt-1">
                    💡 <strong>Tipp:</strong> Nyisd meg a helyi felületet (`http://127.0.0.1:8788`), és nyomd meg a "Környezet Eltérés" gombot — a helyi felület automatikusan beküldi az állapotot az élesnek! Vagy használd a "Pillanatkép beillesztése" gombot fent.
                  </p>
                )}
              </div>
            </div>
          )}

          {loading && !diffData && (
            <div className="py-16 text-center text-slate-500 dark:text-slate-400 space-y-3">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-indigo-600" />
              <p className="text-sm font-medium">Helyi és távoli adatbázis lekérdezése és összehasonlítása folyamatban...</p>
            </div>
          )}

          {diffData && (
            <>
              {/* Forrás/Cél környezet infó */}
              <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 px-1">
                <div>
                  Vizsgálat forrása: <strong className="text-slate-700 dark:text-slate-200">{diffData.stats.remote_url}</strong>
                </div>
                <div>
                  Utolsó ellenőrzés: <strong>{new Date(diffData.stats.checked_at).toLocaleTimeString('hu-HU')}</strong>
                </div>
              </div>

              {/* KPI Összegző Kártyák */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2.5">
                <div className="p-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 rounded-xl text-center">
                  <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                    {isLocalHost ? 'Helyi gép' : isVps ? 'Saját VPS' : 'Éles Felhő'}
                  </div>
                  <div className="text-xl font-bold text-slate-800 dark:text-white mt-0.5">
                    {diffData.stats.local_count}
                  </div>
                  <div className="text-[10px] text-slate-400">szervezet (itt)</div>
                </div>

                <div className="p-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 rounded-xl text-center">
                  <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                    {pointsTo(remoteUrl, DEPLOYMENT.vpsUrl) ? 'VPS Szerver' : remoteUrl.includes('localhost') ? 'Helyi gép' : 'Éles Felhő'}
                  </div>
                  <div className="text-xl font-bold text-slate-800 dark:text-white mt-0.5">
                    {diffData.stats.remote_count}
                  </div>
                  <div className="text-[10px] text-slate-400">szervezet (cél)</div>
                </div>

                <div className={`p-3 border rounded-xl text-center transition ${
                  diffData.stats.local_only_count > 0
                    ? 'bg-rose-50 dark:bg-rose-950/40 border-rose-300 dark:border-rose-900 text-rose-800 dark:text-rose-300'
                    : 'bg-slate-50 dark:bg-slate-800/40 border-slate-200 dark:border-slate-700 text-slate-400'
                }`}>
                  <div className="text-xs font-medium">Csak Itt létezik</div>
                  <div className="text-xl font-bold mt-0.5">{diffData.stats.local_only_count}</div>
                  <div className="text-[10px]">{diffData.stats.local_only_count > 0 ? 'Hiányzik célból' : 'Nincs eltérés'}</div>
                </div>

                <div className={`p-3 border rounded-xl text-center transition ${
                  diffData.stats.remote_only_count > 0
                    ? 'bg-sky-50 dark:bg-sky-950/40 border-sky-300 dark:border-sky-900 text-sky-800 dark:text-sky-300'
                    : 'bg-slate-50 dark:bg-slate-800/40 border-slate-200 dark:border-slate-700 text-slate-400'
                }`}>
                  <div className="text-xs font-medium">Csak Célban van</div>
                  <div className="text-xl font-bold mt-0.5">{diffData.stats.remote_only_count}</div>
                  <div className="text-[10px]">{diffData.stats.remote_only_count > 0 ? 'Hiányzik innen' : 'Nincs eltérés'}</div>
                </div>

                <div className={`p-3 border rounded-xl text-center transition ${
                  diffData.stats.difference_count > 0
                    ? 'bg-amber-50 dark:bg-amber-950/40 border-amber-300 dark:border-amber-900 text-amber-800 dark:text-amber-300'
                    : 'bg-slate-50 dark:bg-slate-800/40 border-slate-200 dark:border-slate-700 text-slate-400'
                }`}>
                  <div className="text-xs font-medium">Mező-eltérés</div>
                  <div className="text-xl font-bold mt-0.5">{diffData.stats.difference_count}</div>
                  <div className="text-[10px]">{diffData.stats.difference_count > 0 ? 'Adatkülönbség' : 'Azonos adatok'}</div>
                </div>

                <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-300 dark:border-emerald-900 rounded-xl text-center text-emerald-800 dark:text-emerald-300">
                  <div className="text-xs font-medium">100% Egyező</div>
                  <div className="text-xl font-bold mt-0.5">{diffData.stats.identical_count}</div>
                  <div className="text-[10px]">Szinkronban</div>
                </div>
              </div>

              {/* Fül-választó sáv */}
              <div className="flex items-center gap-1.5 border-b border-slate-200 dark:border-slate-800 pb-2 overflow-x-auto text-xs font-semibold">
                <button
                  onClick={() => setActiveTab('local_only')}
                  className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition cursor-pointer ${
                    activeTab === 'local_only'
                      ? 'bg-rose-600 text-white shadow-xs'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
                >
                  <XCircle className="w-3.5 h-3.5" />
                  <span>Csak Local-ban ({diffData.local_only.length})</span>
                </button>

                <button
                  onClick={() => setActiveTab('differences')}
                  className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition cursor-pointer ${
                    activeTab === 'differences'
                      ? 'bg-amber-600 text-white shadow-xs'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
                >
                  <AlertTriangle className="w-3.5 h-3.5" />
                  <span>Eltérő adatok ({diffData.differences.length})</span>
                </button>

                <button
                  onClick={() => setActiveTab('remote_only')}
                  className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition cursor-pointer ${
                    activeTab === 'remote_only'
                      ? 'bg-sky-600 text-white shadow-xs'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
                >
                  <Building2 className="w-3.5 h-3.5" />
                  <span>Csak Élesben ({diffData.remote_only.length})</span>
                </button>

                <button
                  onClick={() => setActiveTab('identical')}
                  className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition cursor-pointer ${
                    activeTab === 'identical'
                      ? 'bg-emerald-600 text-white shadow-xs'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>Egyező ({diffData.identical.length})</span>
                </button>

                <button
                  onClick={() => setActiveTab('all')}
                  className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition cursor-pointer ${
                    activeTab === 'all'
                      ? 'bg-slate-800 dark:bg-slate-700 text-white shadow-xs'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
                >
                  <Layers className="w-3.5 h-3.5" />
                  <span>Összes</span>
                </button>
              </div>

              {/* Fül Tartalmak */}
              {/* 1. CSAK LOCAL-BAN */}
              {activeTab === 'local_only' && (
                <div className="space-y-3">
                  {diffData.local_only.length === 0 ? (
                    <div className="py-8 text-center text-slate-500 dark:text-slate-400 text-sm">
                      <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                      Nincs olyan szervezet, ami csak a helyi környezetben létezne.
                    </div>
                  ) : (
                    <>
                      <div className="p-3 bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 rounded-xl text-xs text-rose-800 dark:text-rose-300">
                        <span className="font-bold">Figyelem:</span> Az alábbi szervezetek <strong>csak a helyi adatbázisban léteznek</strong>, az éles rendszerben hiányoznak. Ez a fő oka a 16 vs. 15 darabszámbeli különbségnek!
                      </div>

                      {diffData.local_only.map(t => (
                        <div
                          key={t.id}
                          className="p-4 bg-white dark:bg-slate-800/80 border border-rose-200 dark:border-rose-900/60 rounded-xl shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                        >
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-slate-900 dark:text-white">
                                {t.name}
                              </span>
                              <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                                /{t.slug}
                              </span>
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rose-100 dark:bg-rose-900/50 text-rose-700 dark:text-rose-300">
                                ID: {t.id}
                              </span>
                              {t.is_demo === 1 && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300">
                                  DEMO
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              {t.address || 'Nincs megadott cím'} • {t.email || 'Nincs email'}
                            </p>
                          </div>

                          <div className="flex items-center gap-2">
                            {isLocalHost && (
                              <button
                                onClick={() => handleDeleteLocalTenant(t)}
                                disabled={deletingId === t.id}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/60 dark:hover:bg-rose-900/60 text-rose-700 dark:text-rose-300 border border-rose-300 dark:border-rose-800 rounded-lg text-xs font-semibold transition cursor-pointer shadow-xs disabled:opacity-50"
                                title="Helyi szervezet törlése az élessel való szinkronizáláshoz"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                                <span>{deletingId === t.id ? 'Törlés...' : 'Törlés helyben'}</span>
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}

              {/* 2. ELTÉRŐ ADATOK */}
              {activeTab === 'differences' && (
                <div className="space-y-4">
                  {diffData.differences.length === 0 ? (
                    <div className="py-8 text-center text-slate-500 dark:text-slate-400 text-sm">
                      <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                      Nincs mezőbeli eltérés a közös szervezetek között.
                    </div>
                  ) : (
                    <>
                      <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-xl text-xs text-amber-800 dark:text-amber-300">
                        <span className="font-bold">Információ:</span> Az alábbi szervezetek mindkét adatbázisban léteznek, de bizonyos mezőik eltérnek (pl. a helyi teszt szervezet beszúrása miatt elcsúszott ID-k, vagy eltérő név/cím adatok).
                      </div>

                      {diffData.differences.map(d => (
                        <div
                          key={d.slug}
                          className="p-4 bg-white dark:bg-slate-800/80 border border-amber-200 dark:border-amber-900/60 rounded-xl shadow-xs space-y-3"
                        >
                          <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700 pb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-slate-900 dark:text-white">
                                {d.name}
                              </span>
                              <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                                /{d.slug}
                              </span>
                            </div>
                            <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">
                              {d.diff_fields.length} db mező eltérés
                            </span>
                          </div>

                          <div className="overflow-x-auto">
                            <table className="w-full text-left text-xs border-collapse">
                              <thead>
                                <tr className="border-b border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400">
                                  <th className="py-1.5 px-2">Mező neve</th>
                                  <th className="py-1.5 px-2 bg-indigo-50/50 dark:bg-indigo-950/20 font-semibold text-indigo-700 dark:text-indigo-300">
                                    Local (Helyi érték)
                                  </th>
                                  <th className="py-1.5 px-2 bg-purple-50/50 dark:bg-purple-950/20 font-semibold text-purple-700 dark:text-purple-300">
                                    Éles (Távoli érték)
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {d.diff_fields.map(f => (
                                  <tr key={f.field} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                    <td className="py-2 px-2 font-medium text-slate-700 dark:text-slate-300">
                                      {f.label} <span className="text-[10px] text-slate-400">({f.field})</span>
                                    </td>
                                    <td className="py-2 px-2 font-mono text-indigo-600 dark:text-indigo-400 bg-indigo-50/30 dark:bg-indigo-950/10 font-bold">
                                      {String(f.local_val ?? '–')}
                                    </td>
                                    <td className="py-2 px-2 font-mono text-purple-600 dark:text-purple-400 bg-purple-50/30 dark:bg-purple-950/10 font-bold">
                                      {String(f.remote_val ?? '–')}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}

              {/* 3. CSAK ÉLESBEN */}
              {activeTab === 'remote_only' && (
                <div className="space-y-3">
                  {diffData.remote_only.length === 0 ? (
                    <div className="py-8 text-center text-slate-500 dark:text-slate-400 text-sm">
                      <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                      Nincs olyan szervezet, ami csak az éles rendszerben létezne (minden éles szervezet megvan helyben is).
                    </div>
                  ) : (
                    <>
                      <div className="p-3 bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900 rounded-xl text-xs text-sky-800 dark:text-sky-300">
                        <span className="font-bold">Figyelem:</span> Az alábbi szervezetek <strong>csak az éles rendszerben léteznek</strong>, a helyi adatbázisból hiányoznak.
                      </div>

                      {diffData.remote_only.map(t => (
                        <div
                          key={t.id}
                          className="p-4 bg-white dark:bg-slate-800/80 border border-sky-200 dark:border-sky-900/60 rounded-xl shadow-xs flex items-center justify-between"
                        >
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-slate-900 dark:text-white">
                                {t.name}
                              </span>
                              <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                                /{t.slug}
                              </span>
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300">
                                Éles ID: {t.id}
                              </span>
                            </div>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                              {t.address || 'Nincs cím'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}

              {/* 4. EGYEZŐ SZERVEZETEK */}
              {activeTab === 'identical' && (
                <div className="space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {diffData.identical.map(i => (
                      <div
                        key={i.slug}
                        className="p-3 bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-800 rounded-xl flex items-center justify-between text-xs"
                      >
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                          <div>
                            <div className="font-semibold text-slate-800 dark:text-slate-200">
                              {i.name}
                            </div>
                            <div className="text-[10px] font-mono text-slate-400">
                              /{i.slug} • ID: {i.tenant.id}
                            </div>
                          </div>
                        </div>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300">
                          Szinkronban
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 5. ÖSSZES NÉZET */}
              {activeTab === 'all' && (
                <div className="space-y-2">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="border-b border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400">
                          <th className="py-2 px-2.5">Szervezet Neve</th>
                          <th className="py-2 px-2.5">Slug</th>
                          <th className="py-2 px-2.5">Local Státusz</th>
                          <th className="py-2 px-2.5">Éles Státusz</th>
                          <th className="py-2 px-2.5">Összegzés</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {diffData.all_local.map(loc => {
                          const rem = diffData.all_remote.find(r => r.slug === loc.slug);
                          const isLocalOnly = !rem;
                          const diff = diffData.differences.find(d => d.slug === loc.slug);

                          return (
                            <tr key={loc.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                              <td className="py-2 px-2.5 font-semibold text-slate-800 dark:text-slate-200">
                                {loc.name}
                              </td>
                              <td className="py-2 px-2.5 font-mono text-slate-500">
                                /{loc.slug}
                              </td>
                              <td className="py-2 px-2.5">
                                <span className="font-mono text-indigo-600 dark:text-indigo-400 font-bold">
                                  ID: {loc.id}
                                </span>
                              </td>
                              <td className="py-2 px-2.5">
                                {rem ? (
                                  <span className="font-mono text-purple-600 dark:text-purple-400 font-bold">
                                    ID: {rem.id}
                                  </span>
                                ) : (
                                  <span className="text-rose-500 font-bold">HIÁNYZIK</span>
                                )}
                              </td>
                              <td className="py-2 px-2.5">
                                {isLocalOnly ? (
                                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-rose-100 text-rose-700 dark:bg-rose-900/60 dark:text-rose-300">
                                    Csak Local
                                  </span>
                                ) : diff ? (
                                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-300">
                                    {diff.diff_fields.length} adat eltérés
                                  </span>
                                ) : (
                                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">
                                    Egyező
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Modal Lábléc */}
        <div className="p-4 bg-slate-50 dark:bg-slate-800/80 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopyReport}
              disabled={!diffData}
              className="flex items-center gap-1.5 px-3 py-2 bg-white dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-700 rounded-xl text-xs font-semibold transition cursor-pointer shadow-xs disabled:opacity-50"
              title="Összehasonlító jelentés másolása a vágólapra"
            >
              {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
              <span>{copied ? 'Jelentés kimásolva!' : 'Jelentés másolása (Riport)'}</span>
            </button>

            {isLocalHost && diffData?.all_local && (
              <button
                onClick={handleCopySnapshotJson}
                className="flex items-center gap-1 px-3 py-2 bg-white dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-700 rounded-xl text-xs font-semibold transition cursor-pointer shadow-xs"
                title="Helyi szervezetek JSON másolása"
              >
                {copiedSnapshot ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copiedSnapshot ? 'JSON kimásolva!' : 'Local JSON másolása'}</span>
              </button>
            )}
          </div>

          <button
            onClick={onClose}
            className="px-5 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs font-semibold transition cursor-pointer shadow-xs"
          >
            Bezárás
          </button>
        </div>

      </div>

      {/* Pillanatkép beillesztő segédablak (Éles felületről) */}
      {pasteModalOpen && (
        <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs">
          <div className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-2xl shadow-2xl max-w-lg w-full p-5 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
              <h3 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <ClipboardPaste className="w-5 h-5 text-indigo-600" />
                <span>Helyi (Local) Pillanatkép Beillesztése</span>
              </h3>
              <button onClick={() => setPasteModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-slate-500 dark:text-slate-400">
              Másold ki a helyi felületről a szervezetek JSON-jét a "Local JSON másolása" gombbal, majd illeszd be ide az éles összehasonlításhoz:
            </p>

            <textarea
              rows={6}
              value={pasteJsonText}
              onChange={(e) => setPasteJsonText(e.target.value)}
              placeholder="Illeszd be ide a helyi felületről kimásolt JSON listát..."
              className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl text-xs font-mono text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={() => setPasteModalOpen(false)}
                className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-xl text-xs font-semibold hover:bg-slate-200"
              >
                Mégse
              </button>
              <button
                onClick={handleApplyPastedJson}
                disabled={!pasteJsonText.trim()}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-semibold disabled:opacity-50"
              >
                Összehasonlítás Indítása
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
